"use client"

import { useTheme } from "next-themes"
import Image from "next/image"
import React, { JSX } from "react"

import { newFrontendURL } from "@/lib/new-url"
import { useMounted } from "@/lib/useMounted"

import { Separator } from "@/components/ui/separator"

import { Config } from "@/types/nango"

type NavigationItem = {
  name: string
  href: string
  icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
}

export default function Footer({ config }: { config: Config }) {
  const { theme } = useTheme()
  const mounted = useMounted()
  const navigation: { connect: NavigationItem[]; company: NavigationItem[] } = {
    connect: [
      {
        name: "Github",
        href: "https://github.com/grezy-software/",
      },
      {
        name: "Tom's LinkedIn",
        href: "https://www.linkedin.com/in/tomjeannesson/",
      },
      {
        name: "Nicolas's LinkedIn",
        href: "https://www.linkedin.com/in/nfaglin/",
      },
    ],
    company: [
      { name: "Grezy", href: "https://grezy.org/" },
      {
        name: "Terms of Service",
        href: newFrontendURL(config, "/terms", false),
      },
      {
        name: "Privacy Policy",
        href: newFrontendURL(config, "/privacy", false),
      },
    ],
  }
  return (
    <footer
      aria-labelledby="footer-heading"
      className="container pt-10 pb-12 sm:pt-20"
    >
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="">
        <div className="flex flex-col justify-between lg:flex-row">
          <div className="space-y-8">
            {mounted && (
              <>
                {theme === "dark" ? (
                  <Image
                    priority={true}
                    width={50}
                    height={50}
                    src={"/grezy/dark.png"}
                    alt="logo"
                    className="h-7 w-auto rounded"
                  />
                ) : (
                  <Image
                    priority={true}
                    width={50}
                    height={50}
                    src={"/grezy/light.png"}
                    alt="logo"
                    className="h-7 w-auto rounded"
                  />
                )}
              </>
            )}
            <p className="text-md max-w-sm text-justify leading-6">
              This tool was built by Tom JEANNESSON and Nicolas FAGLIN,
              co-founders of{" "}
              <a
                href="https://grezy.org/"
                className="font-bold hover:underline"
              >
                Grezy Software
              </a>
              .
            </p>
          </div>

          <div className="mt-16 grid grid-cols-2 gap-14 md:grid-cols-2 lg:mt-0 xl:col-span-2">
            <div className="md:mt-0">
              <h3 className="text-sm leading-6 font-semibold">Contact</h3>
              <div className="mt-6 space-y-4">
                {navigation.connect.map((item) => (
                  <div key={item.name}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm leading-6"
                    >
                      {item.name}
                    </a>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div>
                <h3 className="text-sm leading-6 font-semibold">Company</h3>
                <div className="mt-6 space-y-4">
                  {navigation.company.map((item) => (
                    <div key={item.name}>
                      <a href={item.href} className="text-sm leading-6">
                        {item.name}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Separator className="my-8" />
        <div>
          <p className="text-xs leading-5">
            &copy; {new Date().getFullYear()} Grezy Software. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
